import React, { useState } from "react";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  TouchSensor,
  MouseSensor,
} from "@dnd-kit/core";
import {
  rectSortingStrategy,
  SortableContext,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import Modal from "components/ui/Modal";
import SearchSelect from "components/ui/SearchSelect";
import AudienceSelect, {
  toAudienceValue,
  fromAudienceValue,
  audienceRules,
} from "./AudienceSelect";
import { useEventTypesQuery } from "apis/eventType";
import { useContractorCompaniesQuery } from "apis/contractorCompany";

// Static SearchSelect options ({ label, value } shape)
const PRODUCT_TYPE_OPTIONS = [
  { value: "subscription", label: "Subscription" },
  { value: "transactional", label: "Transactional" },
  { value: "marketing", label: "Marketing" },
];

const BILLING_CYCLE_OPTIONS = [
  { value: "monthly", label: "Monthly" },
  { value: "yearly", label: "Yearly" },
  { value: "bespoke", label: "Bespoke" },
];

const CONTRACTOR_FEE_TYPE_OPTIONS = [
  { value: "", label: "No Contractor Fee" },
  { value: "fixed", label: "Fixed Amount" },
  { value: "percentage", label: "Percentage" },
];

// Error-border helper (SearchSelect has no `styles` prop like react-select)
const errorBorderClass = (fieldError) => (fieldError ? "border-red-500" : "");

// "extraLarge" -> "Extra Large" (same as campaignEvents/Edit.js)
const formatSize = (size) => {
  if (!size) return "";
  return size
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/^./, (c) => c.toUpperCase());
};

// Sortable Feature Item Component
function SortableFeatureItem({
  field,
  index,
  register,
  errors,
  onRemove,
  canRemove,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex gap-2 items-center ${isDragging ? "z-10" : ""}`}
    >
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing p-2 hover:bg-lightgray rounded"
        title="Drag to reorder"
      >
        <svg
          className="w-4 h-4 text-darkgray"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M3 4h14c.55 0 1-.45 1-1s-.45-1-1-1H3c-.55 0-1 .45-1 1s.45 1 1 1zm0 4h14c.55 0 1-.45 1-1s-.45-1-1-1H3c-.55 0-1 .45-1 1s.45 1 1 1zm0 4h14c.55 0 1-.45 1-1s-.45-1-1-1H3c-.55 0-1 .45-1 1s.45 1 1 1zm0 4h14c.55 0 1-.45 1-1s-.45-1-1-1H3c-.55 0-1 .45-1 1s.45 1 1 1z" />
        </svg>
      </div>
      <input
        type="text"
        {...register(`features.${index}.value`)}
        placeholder={`Feature ${index + 1}`}
        className={`form-input flex-1 border ${
          errors.features?.[index]?.value
            ? "border-red-500"
            : "border-mediumgray"
        }`}
      />
      <button
        type="button"
        onClick={() => onRemove(index)}
        disabled={!canRemove}
        className={`px-3 py-2 rounded-md text-sm font-medium ${
          !canRemove
            ? "bg-lightgray text-darkgray cursor-not-allowed"
            : "bg-red-100 text-red-700 hover:bg-red-200"
        }`}
      >
        Remove
      </button>
    </div>
  );
}

// Sortable Terms Item Component
function SortableTermItem({
  field,
  index,
  register,
  errors,
  onRemove,
  canRemove,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex gap-2 items-center ${isDragging ? "z-10" : ""}`}
    >
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing p-2 hover:bg-lightgray rounded"
        title="Drag to reorder"
      >
        <svg
          className="w-4 h-4 text-darkgray"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M3 4h14c.55 0 1-.45 1-1s-.45-1-1-1H3c-.55 0-1 .45-1 1s.45 1 1 1zm0 4h14c.55 0 1-.45 1-1s-.45-1-1-1H3c-.55 0-1 .45-1 1s.45 1 1 1zm0 4h14c.55 0 1-.45 1-1s-.45-1-1-1H3c-.55 0-1 .45-1 1s.45 1 1 1zm0 4h14c.55 0 1-.45 1-1s-.45-1-1-1H3c-.55 0-1 .45-1 1s.45 1 1 1z" />
        </svg>
      </div>
      <input
        type="text"
        {...register(`terms.${index}.value`)}
        placeholder={`Term ${index + 1}`}
        className={`form-input flex-1 border ${
          errors.terms?.[index]?.value ? "border-red-500" : "border-mediumgray"
        }`}
      />
      <button
        type="button"
        onClick={() => onRemove(index)}
        disabled={!canRemove}
        className={`px-3 py-2 rounded-md text-sm font-medium ${
          !canRemove
            ? "bg-lightgray text-darkgray cursor-not-allowed"
            : "bg-red-100 text-red-700 hover:bg-red-200"
        }`}
      >
        Remove
      </button>
    </div>
  );
}

// Section wrapper — heading + divider so the long form reads as grouped steps
function FormSection({ title, description, action, children }) {
  return (
    <section className="space-y-4">
      <div className="flex items-end justify-between gap-4 border-b border-mediumgray pb-2">
        <div>
          <h4>{title}</h4>
          {description && (
            <p className="text-sm text-darkgray mt-0.5">{description}</p>
          )}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

// Top-aligned checkbox + label/description (a centered checkbox floats next
// to multi-line descriptions)
function CheckboxRow({ id, label, description, inputProps }) {
  return (
    <div className="flex items-start gap-3">
      <input
        type="checkbox"
        id={id}
        className="h-4 w-4 mt-0.5 flex-shrink-0 cursor-pointer"
        {...inputProps}
      />
      <div>
        <label htmlFor={id} className="font-medium cursor-pointer">
          {label}
        </label>
        {description && (
          <p className="text-sm text-darkergray mt-0.5">{description}</p>
        )}
      </div>
    </div>
  );
}

export default function ProductEdit({
  initialData = {},
  onSubmit,
  existingProducts = [],
}) {
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copyType, setCopyType] = useState("features");

  // Notify emails tag-input state
  const [notifyEmails, setNotifyEmails] = useState(
    Array.isArray(initialData.notifyEmails) ? initialData.notifyEmails : [],
  );
  const [notifyEmailInput, setNotifyEmailInput] = useState("");

  const { data: eventTypes, isLoading: isEventTypesLoading } =
    useEventTypesQuery();

  const { data: companies, isLoading: isCompaniesLoading } =
    useContractorCompaniesQuery("active");

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      // Scheduled-work section (create mode only) — auto-creates a campaign
      // event alongside the product instead of the old manual link dropdown.
      eventTypeId: "",
      eventQuantity: 1,
      eventSize: "",
      eventDurationMinutes: "",
      name: initialData.name || "",
      shortName: initialData.shortName || "",
      description: initialData.description || "",
      type: initialData.type || "subscription",
      price: initialData.price || "",
      priceDisplay: initialData.priceDisplay || "",
      billingCycle: initialData.billingCycle || "monthly",
      features:
        initialData.features?.length > 0
          ? initialData.features.map((feature) => ({ value: feature }))
          : [{ value: "" }],
      terms:
        initialData.terms?.length > 0
          ? initialData.terms.map((term) => ({ value: term }))
          : [{ value: "" }],
      isPopular: initialData.isPopular || false,
      isSystem: initialData.isSystem || false,
      stripePriceId: initialData.stripePriceId || "",
      maxCampaigns: initialData.maxCampaigns || "",
      category: initialData.category || "",
      contractorFeeType: initialData.contractorFee?.type || "",
      contractorFeeValue: initialData.contractorFee?.value || "",
      contractorCompanyId:
        initialData.contractorCompanyId?._id ||
        initialData.contractorCompanyId ||
        "",
      enableOrderNotes: initialData.enableOrderNotes || false,
      metadataSize: initialData.metadata?.size || "",
      metadataQuantity: initialData.metadata?.quantity ?? "",
      metadataItemCode: initialData.metadata?.itemCode || "",
      metadataDesign: initialData.metadata?.design || "",
      audience: toAudienceValue(initialData.offices),
      isFreeInclusion: initialData.isFreeInclusion || false,
      freeWithCategories: initialData.freeWithCategories || [],
    },
  });

  const productType = watch("type");
  const contractorFeeType = watch("contractorFeeType");
  const contractorCompanyId = watch("contractorCompanyId");
  const isFreeInclusion = watch("isFreeInclusion");
  const eventTypeId = watch("eventTypeId");
  const eventQuantity = watch("eventQuantity");
  const eventSize = watch("eventSize");

  // Options for the company SearchSelect ({ label, value } shape)
  const companyOptions = (companies || []).map((c) => ({
    label: c.name,
    value: c._id,
  }));

  // Scheduled-work event types — only these get a campaign event.
  const isCreateMode = !initialData._id;
  const schedulableEventTypes = (eventTypes || []).filter(
    (e) => e.requiresContractor && !e.isDeleted,
  );
  const eventTypeOptions = schedulableEventTypes.map((e) => ({
    label: `${e.name} (${e.contractorService})`,
    value: e._id,
  }));
  const selectedEventType =
    schedulableEventTypes.find((e) => e._id === eventTypeId) || null;

  // Same auto-name the campaign events screen generates.
  const autoEventName = selectedEventType
    ? [
        selectedEventType.name,
        eventSize ? formatSize(eventSize) : null,
        Number(eventQuantity) > 1 ? `x${Number(eventQuantity)}` : null,
      ]
        .filter(Boolean)
        .join(" ")
    : "";

  const {
    fields: featureFields,
    append: appendFeature,
    remove: removeFeatureField,
    move: moveFeature,
  } = useFieldArray({
    control,
    name: "features",
  });

  const {
    fields: termFields,
    append: appendTerm,
    remove: removeTermField,
    move: moveTerm,
  } = useFieldArray({
    control,
    name: "terms",
  });

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 8 },
    }),
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
  );

  const submitForm = (data) => {
    const transformedData = {
      ...data,
      contractorCompanyId: data.contractorCompanyId || null,
      features: data.features
        .map((feature) => feature.value)
        .filter((f) => f.trim() !== ""),
      terms: data.terms
        .map((term) => term.value)
        .filter((t) => t.trim() !== ""),
      price: data.price ? Number(data.price) : null,
      maxCampaigns:
        data.maxCampaigns && data.maxCampaigns !== ""
          ? Number(data.maxCampaigns)
          : null,
      enableOrderNotes: !!data.enableOrderNotes,
      isSystem: !!data.isSystem,
      isFreeInclusion: !!data.isFreeInclusion,
      // Stringified like notifyEmails/offices — multipart can't carry arrays.
      // Turning the toggle off clears the triggers too, so a later re-enable
      // can't silently fire on categories nobody looked at.
      freeWithCategories: JSON.stringify(
        data.isFreeInclusion ? data.freeWithCategories || [] : [],
      ),
      notifyEmails: JSON.stringify(notifyEmails),
      // Stringified like notifyEmails — the payload goes out as multipart,
      // which can't carry arrays natively. Empty = offered to all offices.
      offices: JSON.stringify(fromAudienceValue(data.audience)),
      // Print size / run quantity — stringified for multipart like the arrays
      // above. Always sent, so clearing the fields clears the stored value.
      metadata: JSON.stringify({
        ...(data.metadataSize?.trim()
          ? { size: data.metadataSize.trim() }
          : {}),
        ...(data.metadataQuantity !== "" && data.metadataQuantity != null
          ? { quantity: Number(data.metadataQuantity) }
          : {}),
        ...(data.metadataItemCode?.trim()
          ? { itemCode: data.metadataItemCode.trim() }
          : {}),
        ...(data.metadataDesign?.trim()
          ? { design: data.metadataDesign.trim() }
          : {}),
      }),
    };

    if (data.contractorFeeType && data.contractorFeeValue) {
      transformedData.contractorFee = {
        type: data.contractorFeeType,
        value: Number(data.contractorFeeValue),
      };
    }

    delete transformedData.contractorFeeType;
    delete transformedData.contractorFeeValue;
    delete transformedData.audience; // form-only wrapper around `offices`
    delete transformedData.metadataSize; // form-only wrappers around `metadata`
    delete transformedData.metadataQuantity;
    delete transformedData.metadataItemCode;
    delete transformedData.metadataDesign;

    if (data.type === "transactional" || data.type === "marketing") {
      transformedData.billingCycle = null;
      transformedData.maxCampaigns = null;
    }

    // Scheduled work: the page creates this campaign event first, then the
    // product with its id (rolling back the event if the product fails).
    // Not part of the product payload — Products.js strips it before POSTing.
    if (isCreateMode && selectedEventType) {
      transformedData.__campaignEvent = {
        eventTypeId: selectedEventType._id,
        name: autoEventName,
        quantity: data.eventQuantity ? Number(data.eventQuantity) : 1,
        size: data.eventSize || null,
        durationHours:
          selectedEventType.requiresDuration !== false &&
          data.eventDurationMinutes
            ? Number(data.eventDurationMinutes) / 60
            : null,
        contractorCompanyId: data.contractorCompanyId || null,
        // Identical work already on offer (another price tier) → share that
        // event instead of duplicating it.
        reuseExisting: true,
      };
    }
    delete transformedData.eventTypeId;
    delete transformedData.eventQuantity;
    delete transformedData.eventSize;
    delete transformedData.eventDurationMinutes;

    onSubmit(transformedData);
  };

  const handleFeatureDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = featureFields.findIndex(
        (field) => field.id === active.id,
      );
      const newIndex = featureFields.findIndex((field) => field.id === over.id);
      moveFeature(oldIndex, newIndex);
    }
  };

  const handleTermDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = termFields.findIndex((field) => field.id === active.id);
      const newIndex = termFields.findIndex((field) => field.id === over.id);
      moveTerm(oldIndex, newIndex);
    }
  };

  const addFeature = () => appendFeature({ value: "" });
  const removeFeature = (index) => {
    if (featureFields.length > 1) removeFeatureField(index);
  };
  const addTerm = () => appendTerm({ value: "" });
  const removeTerm = (index) => {
    if (termFields.length > 1) removeTermField(index);
  };

  const addNotifyEmail = () => {
    const email = notifyEmailInput.trim().toLowerCase();
    if (!email) return;
    // support comma-separated paste
    const emails = email
      .split(",")
      .map((e) => e.trim())
      .filter((e) => e && !notifyEmails.includes(e));
    if (emails.length) setNotifyEmails((prev) => [...prev, ...emails]);
    setNotifyEmailInput("");
  };

  const removeNotifyEmail = (index) => {
    setNotifyEmails((prev) => prev.filter((_, i) => i !== index));
  };

  const copyFromProduct = (productId, type) => {
    const product = existingProducts.find((p) => p._id === productId);
    if (!product) return;

    if (type === "features" && product.features) {
      const currentLength = featureFields.length;
      for (let i = currentLength - 1; i >= 0; i--) removeFeatureField(i);
      product.features.forEach((feature) => appendFeature({ value: feature }));
    } else if (type === "terms" && product.terms) {
      const currentLength = termFields.length;
      for (let i = currentLength - 1; i >= 0; i--) removeTermField(i);
      product.terms.forEach((term) => appendTerm({ value: term }));
    }

    setShowCopyModal(false);
  };

  const openCopyModal = (type) => {
    setCopyType(type);
    setShowCopyModal(true);
  };

  const getCategoryOptions = () => {
    if (productType === "subscription") {
      return [
        { value: "campaign", label: "Campaign" },
        { value: "social", label: "Social" },
        { value: "community", label: "Community" },
        { value: "other", label: "Other" },
      ];
    } else if (productType === "transactional") {
      return [
        { value: "gift", label: "Gift" },
        { value: "sms", label: "SMS" },
        { value: "other", label: "Other" },
      ];
    } else {
      return [
        { value: "photo", label: "Photo" },
        { value: "video", label: "Video" },
        { value: "floorplan", label: "Floorplan" },
        { value: "photoReshoot", label: "Photo Reshoot" },
        { value: "virtualFurniture", label: "Virtual Furniture" },
        { value: "declutter", label: "Declutter" },
        { value: "flipping", label: "Flipping" },
        { value: "dusk", label: "Dusk" },
        { value: "duskGolden", label: "Dusk Golden" },
        { value: "drone", label: "Drone" },
        { value: "mailcard", label: "Mailcard" },
        { value: "brochure", label: "Brochure" },
        { value: "signboard", label: "Signboard" },
        { value: "contentMarketing", label: "Content Marketing" },
        { value: "internetPortals", label: "Internet Portals" },
        { value: "auctioneer", label: "Auctioneer" },
        { value: "imGroup", label: "I.M Group" },
        { value: "other", label: "Other" },
      ];
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit(submitForm)} className="space-y-8">
        <FormSection title="Basic Information">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Product Name */}
            <div className="space-y-2">
              <label htmlFor="name" className="form-label">
                Product Name *
              </label>
              <input
                type="text"
                id="name"
                {...register("name", { required: "Product name is required" })}
                placeholder="e.g., Basic Plan, Pro Plan, Gift Card"
                className={`form-input border ${
                  errors.name ? "border-red-500" : "border-mediumgray"
                }`}
              />
              {errors.name && (
                <p className="form-error-message">{errors.name.message}</p>
              )}
            </div>

            {/* Short Name */}
            <div className="space-y-2">
              <label htmlFor="shortName" className="form-label">
                Short Name *
              </label>
              <input
                type="text"
                id="shortName"
                {...register("shortName", {
                  required: "Short name is required",
                })}
                placeholder="e.g., Basic, Pro, Gift"
                className={`form-input border ${
                  errors.shortName ? "border-red-500" : "border-mediumgray"
                }`}
              />
              {errors.shortName && (
                <p className="form-error-message">{errors.shortName.message}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Product Type */}
            <div className="space-y-2">
              <label className="form-label">Product Type *</label>
              <Controller
                name="type"
                control={control}
                rules={{ required: "Product type is required" }}
                render={({ field }) => (
                  <SearchSelect
                    options={PRODUCT_TYPE_OPTIONS}
                    value={
                      PRODUCT_TYPE_OPTIONS.find(
                        (o) => o.value === field.value,
                      ) || null
                    }
                    onChange={(option) => field.onChange(option?.value || "")}
                    placeholder="Select a product type"
                    searchable={false}
                    className={errorBorderClass(errors.type)}
                  />
                )}
              />
              {errors.type && (
                <p className="form-error-message">{errors.type.message}</p>
              )}
            </div>

            {/* Category */}
            <div className="space-y-2">
              <label className="form-label">Category *</label>
              <Controller
                name="category"
                control={control}
                rules={{ required: "Category is required" }}
                render={({ field }) => (
                  <SearchSelect
                    options={getCategoryOptions()}
                    value={
                      getCategoryOptions().find(
                        (o) => o.value === field.value,
                      ) || null
                    }
                    onChange={(option) => field.onChange(option?.value || "")}
                    placeholder="Select a category"
                    className={errorBorderClass(errors.category)}
                  />
                )}
              />
              {errors.category && (
                <p className="form-error-message">{errors.category.message}</p>
              )}
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <label htmlFor="description" className="form-label">
              Description *
            </label>
            <textarea
              id="description"
              {...register("description", {
                required: "Description is required",
              })}
              placeholder="Describe this product..."
              rows={4}
              className={`form-textarea border ${
                errors.description ? "border-red-500" : "border-mediumgray"
              }`}
            />
            {errors.description && (
              <p className="form-error-message">{errors.description.message}</p>
            )}
          </div>
        </FormSection>

        {/* Campaign event — auto-created with the product (create mode), or
            shown read-only (edit mode). The manual link dropdown is gone: the
            event follows the product's lifecycle (delete archives it, restore
            brings it back). */}
        {!isCreateMode
          ? initialData.campaignEventId && (
              <div className="space-y-1 border border-mediumgray rounded-lg p-4 bg-lightergray">
                <label className="form-label">Linked Campaign Event</label>
                <p className="text-sm font-medium">
                  {initialData.campaignEventId?.name || "Campaign event"}
                </p>
                <p className="text-sm text-darkgray">
                  Created automatically with this product. It archives and
                  restores together with the product; manage its details on the
                  Campaign Events page.
                </p>
              </div>
            )
          : productType === "marketing" && (
              <div className="border border-mediumgray rounded-lg p-4 bg-lightergray space-y-4">
                <div>
                  <h4>Scheduled Work (Optional)</h4>
                  <p className="text-sm text-darkgray mt-1">
                    Pick an event type if booking this product should schedule a
                    contractor. A campaign event is created automatically with
                    the product — and archived with it on delete.
                  </p>
                </div>

                <div className="space-y-2">
                  <label className="form-label">Event Type</label>
                  <Controller
                    name="eventTypeId"
                    control={control}
                    render={({ field }) => (
                      <SearchSelect
                        options={eventTypeOptions}
                        value={
                          eventTypeOptions.find(
                            (o) => o.value === field.value,
                          ) || null
                        }
                        onChange={(option) => {
                          field.onChange(option?.value || "");
                          setValue("eventSize", "");
                          const et = schedulableEventTypes.find(
                            (e) => e._id === option?.value,
                          );
                          if (et?.requiresDuration !== false) {
                            setValue(
                              "eventDurationMinutes",
                              et?.defaultDurationMinutes || "",
                            );
                          }
                        }}
                        placeholder={
                          isEventTypesLoading
                            ? "Loading..."
                            : "None (no scheduled event)"
                        }
                        isClearable
                        disabled={isEventTypesLoading}
                      />
                    )}
                  />
                </div>

                {selectedEventType && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="form-label">Quantity</label>
                        <input
                          type="number"
                          min={1}
                          {...register("eventQuantity", {
                            validate: (v) =>
                              !selectedEventType ||
                              Number(v) >= 1 ||
                              "Enter a quantity",
                          })}
                          className={`form-input border ${
                            errors.eventQuantity
                              ? "border-red-500"
                              : "border-mediumgray"
                          }`}
                        />
                        {errors.eventQuantity && (
                          <p className="form-error-message">
                            {errors.eventQuantity.message}
                          </p>
                        )}
                      </div>

                      {selectedEventType.requiresDuration !== false && (
                        <div className="space-y-2">
                          <label className="form-label">
                            Duration (minutes)
                          </label>
                          <input
                            type="number"
                            min={5}
                            step={5}
                            placeholder="e.g. 30"
                            {...register("eventDurationMinutes", {
                              validate: (v) => {
                                if (
                                  !selectedEventType ||
                                  selectedEventType.requiresDuration === false
                                )
                                  return true;
                                const n = Number(v);
                                if (!(n >= 5))
                                  return "Enter a duration in minutes";
                                if (n > 720)
                                  return "Maximum duration is 720 minutes (12 hours)";
                                return true;
                              },
                            })}
                            className={`form-input border ${
                              errors.eventDurationMinutes
                                ? "border-red-500"
                                : "border-mediumgray"
                            }`}
                          />
                          {errors.eventDurationMinutes && (
                            <p className="form-error-message">
                              {errors.eventDurationMinutes.message}
                            </p>
                          )}
                        </div>
                      )}
                    </div>

                    {(selectedEventType.sizeOptions || []).length > 0 && (
                      <div className="space-y-2">
                        <label className="form-label">Size</label>
                        <Controller
                          name="eventSize"
                          control={control}
                          rules={{
                            validate: (v) =>
                              !selectedEventType ||
                              (selectedEventType.sizeOptions || []).length ===
                                0 ||
                              !!v ||
                              "Choose a size",
                          }}
                          render={({ field }) => (
                            <SearchSelect
                              options={(
                                selectedEventType.sizeOptions || []
                              ).map((s) => ({
                                value: s,
                                label: formatSize(s),
                              }))}
                              value={
                                field.value
                                  ? {
                                      value: field.value,
                                      label: formatSize(field.value),
                                    }
                                  : null
                              }
                              onChange={(option) =>
                                field.onChange(option?.value || "")
                              }
                              placeholder="Choose a size..."
                            />
                          )}
                        />
                        {errors.eventSize && (
                          <p className="form-error-message">
                            {errors.eventSize.message}
                          </p>
                        )}
                      </div>
                    )}

                    {autoEventName && (
                      <p className="text-sm text-darkgray">
                        Campaign event will be created as{" "}
                        <span className="font-medium">"{autoEventName}"</span>
                        {!contractorCompanyId &&
                          " — set a contractor company above so it's scoped to the right company"}
                        .
                      </p>
                    )}
                  </>
                )}
              </div>
            )}

        <FormSection title="Pricing">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Price */}
            <div className="space-y-2">
              <label htmlFor="price" className="form-label">
                Price (Numeric)
              </label>
              <input
                type="number"
                step="0.01"
                id="price"
                {...register("price")}
                placeholder="29.99"
                className={`form-input border ${
                  errors.price ? "border-red-500" : "border-mediumgray"
                }`}
              />
            </div>

            {/* Price Display */}
            <div className="space-y-2">
              <label htmlFor="priceDisplay" className="form-label">
                Price Display Text
              </label>
              <input
                type="text"
                id="priceDisplay"
                {...register("priceDisplay")}
                placeholder={
                  productType === "subscription" ? "$29.99/month" : "$99.99"
                }
                className={`form-input border ${
                  errors.priceDisplay ? "border-red-500" : "border-mediumgray"
                }`}
              />
            </div>

            {/* Billing cycle + campaign limit — subscriptions only */}
            {productType === "subscription" && (
              <>
                <div className="space-y-2">
                  <label className="form-label">Billing Cycle *</label>
                  <Controller
                    name="billingCycle"
                    control={control}
                    rules={{
                      required:
                        productType === "subscription"
                          ? "Billing cycle is required"
                          : false,
                    }}
                    render={({ field }) => (
                      <SearchSelect
                        options={BILLING_CYCLE_OPTIONS}
                        value={
                          BILLING_CYCLE_OPTIONS.find(
                            (o) => o.value === field.value,
                          ) || null
                        }
                        onChange={(option) =>
                          field.onChange(option?.value || "")
                        }
                        placeholder="Select a billing cycle"
                        searchable={false}
                        className={errorBorderClass(errors.billingCycle)}
                      />
                    )}
                  />
                  {errors.billingCycle && (
                    <p className="form-error-message">
                      {errors.billingCycle.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <label htmlFor="maxCampaigns" className="form-label">
                    Max Campaigns{" "}
                    <span className="font-light">Put -1 for unlimited</span>
                  </label>
                  <input
                    type="number"
                    step="1"
                    id="maxCampaigns"
                    {...register("maxCampaigns")}
                    placeholder="Leave empty for unlimited"
                    className={`form-input border ${
                      errors.maxCampaigns
                        ? "border-red-500"
                        : "border-mediumgray"
                    }`}
                  />
                </div>
              </>
            )}

            {/* Stripe Price ID */}
            <div className="space-y-2">
              <label htmlFor="stripePriceId" className="form-label">
                Stripe Price ID
              </label>
              <input
                type="text"
                id="stripePriceId"
                {...register("stripePriceId")}
                placeholder="price_1234567890"
                className={`form-input border ${
                  errors.stripePriceId ? "border-red-500" : "border-mediumgray"
                }`}
              />
            </div>
          </div>
        </FormSection>

        {/* Print details — the size token / run quantity marketing templates
            resolve against (e.g. signboard 8x4, brochure a4). */}
        <FormSection
          title="Print Details"
          description="Optional — used by print marketing products like signboards, brochures and mailcards."
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label htmlFor="metadataSize" className="form-label">
                Print Size
              </label>
              <input
                type="text"
                id="metadataSize"
                {...register("metadataSize")}
                placeholder="e.g., 8x4, 6x4, a4, a5, landscape, portrait"
                className="form-input border border-mediumgray"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="metadataQuantity" className="form-label">
                Print Quantity
              </label>
              <input
                type="number"
                min={1}
                step={1}
                id="metadataQuantity"
                {...register("metadataQuantity")}
                placeholder="e.g., 1, 50, 1000"
                className="form-input border border-mediumgray"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="metadataDesign" className="form-label">
                Design
              </label>
              <input
                type="text"
                id="metadataDesign"
                {...register("metadataDesign")}
                placeholder="e.g., sale, photo"
                className="form-input border border-mediumgray"
              />
              <p className="text-xs text-gray-500">
                Disambiguates products that share the same Print Size but are
                a different design (e.g. Stone's "sale" vs "photo" 6x4
                signboards) — matched against a template's
                applicability.design.
              </p>
            </div>

            <div className="space-y-2">
              <label htmlFor="metadataItemCode" className="form-label">
                Contractor Item Code
              </label>
              <input
                type="text"
                id="metadataItemCode"
                {...register("metadataItemCode")}
                placeholder="e.g., 287639, S10102-50"
                className="form-input border border-mediumgray"
              />
            </div>
          </div>
        </FormSection>

        {/* Contractor Section */}
        <FormSection
          title="Contractor"
          description="Optional. The company that fulfils this product, and its fee."
        >
          {/* Contractor Company */}
          <div className="space-y-2">
            <label className="form-label">Contractor Company</label>
            <Controller
              name="contractorCompanyId"
              control={control}
              render={({ field }) => (
                <SearchSelect
                  options={companyOptions}
                  value={
                    companyOptions.find((o) => o.value === field.value) || null
                  }
                  onChange={(option) => field.onChange(option?.value || "")}
                  placeholder={
                    isCompaniesLoading ? "Loading..." : "Select a company"
                  }
                  isClearable
                  disabled={isCompaniesLoading}
                />
              )}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="form-label">Fee Type</label>
              <Controller
                name="contractorFeeType"
                control={control}
                render={({ field }) => (
                  <SearchSelect
                    options={CONTRACTOR_FEE_TYPE_OPTIONS}
                    value={
                      CONTRACTOR_FEE_TYPE_OPTIONS.find(
                        (o) => o.value === field.value,
                      ) || null
                    }
                    onChange={(option) => field.onChange(option?.value || "")}
                    placeholder="No Contractor Fee"
                    searchable={false}
                    className={errorBorderClass(errors.contractorFeeType)}
                  />
                )}
              />
              {errors.contractorFeeType && (
                <p className="form-error-message">
                  {errors.contractorFeeType.message}
                </p>
              )}
            </div>

            {contractorFeeType && (
              <div className="space-y-2">
                <label htmlFor="contractorFeeValue" className="form-label">
                  Fee Value *{" "}
                  <span className="text-darkgray font-normal">
                    {contractorFeeType === "percentage" ? "(0-100)" : "($)"}
                  </span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  id="contractorFeeValue"
                  {...register("contractorFeeValue", {
                    required: contractorFeeType
                      ? "Fee value is required when fee type is selected"
                      : false,
                    min: {
                      value: 0,
                      message: "Fee value must be positive",
                    },
                    max:
                      contractorFeeType === "percentage"
                        ? {
                            value: 100,
                            message: "Percentage cannot exceed 100",
                          }
                        : undefined,
                  })}
                  placeholder={
                    contractorFeeType === "percentage" ? "15" : "50.00"
                  }
                  className={`form-input border ${
                    errors.contractorFeeValue
                      ? "border-red-500"
                      : "border-mediumgray"
                  }`}
                />
                {errors.contractorFeeValue && (
                  <p className="form-error-message">
                    {errors.contractorFeeValue.message}
                  </p>
                )}
              </div>
            )}
          </div>
        </FormSection>

        {/* Available to (office audience) */}
        <FormSection
          title="Availability"
          description="Which offices can see and purchase this product."
        >
          <div className="space-y-2">
            <Controller
              name="audience"
              control={control}
              rules={audienceRules}
              render={({ field, fieldState }) => (
                <AudienceSelect
                  value={field.value}
                  onChange={field.onChange}
                  error={fieldState.error?.message}
                />
              )}
            />
          </div>
        </FormSection>

        {/* Features with Drag & Drop */}
        <FormSection
          title="Features"
          description="The selling points shown with this product. Drag to reorder."
          action={
            existingProducts.length > 0 && (
              <button
                type="button"
                onClick={() => openCopyModal("features")}
                className="gray-button whitespace-nowrap"
              >
                Copy from Product
              </button>
            )
          }
        >
          <div className="space-y-2">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleFeatureDragEnd}
            >
              <SortableContext
                items={featureFields.map((field) => field.id)}
                strategy={rectSortingStrategy}
              >
                <div className="space-y-3">
                  {featureFields.map((field, index) => (
                    <SortableFeatureItem
                      key={field.id}
                      field={field}
                      index={index}
                      register={register}
                      errors={errors}
                      onRemove={removeFeature}
                      canRemove={featureFields.length > 1}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>

            {errors.features?.[0]?.value && (
              <p className="form-error-message">
                {errors.features[0].value.message}
              </p>
            )}

            <button
              type="button"
              onClick={addFeature}
              className="mt-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-md text-sm font-medium hover:bg-blue-200"
            >
              Add Feature
            </button>
          </div>
        </FormSection>

        {/* Terms with Drag & Drop */}
        <FormSection
          title="Terms"
          description="Terms & conditions attached to this product. Drag to reorder."
          action={
            existingProducts.length > 0 && (
              <button
                type="button"
                onClick={() => openCopyModal("terms")}
                className="gray-button whitespace-nowrap"
              >
                Copy from Product
              </button>
            )
          }
        >
          <div className="space-y-2">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleTermDragEnd}
            >
              <SortableContext
                items={termFields.map((field) => field.id)}
                strategy={rectSortingStrategy}
              >
                <div className="space-y-3">
                  {termFields.map((field, index) => (
                    <SortableTermItem
                      key={field.id}
                      field={field}
                      index={index}
                      register={register}
                      errors={errors}
                      onRemove={removeTerm}
                      canRemove={termFields.length > 1}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>

            {errors.terms?.[0]?.value && (
              <p className="form-error-message">
                {errors.terms[0].value.message}
              </p>
            )}

            <button
              type="button"
              onClick={addTerm}
              className="mt-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-md text-sm font-medium hover:bg-blue-200"
            >
              Add Term
            </button>
          </div>
        </FormSection>

        {/* Options */}
        <FormSection title="Options">
          <CheckboxRow
            id="isPopular"
            label="Mark as Popular Product"
            inputProps={register("isPopular")}
          />

          <CheckboxRow
            id="enableOrderNotes"
            label="Enable Order Notes"
            description="Allow customers to add notes when purchasing this product."
            inputProps={register("enableOrderNotes")}
          />

          <CheckboxRow
            id="isSystem"
            label="System Product"
            description="Managed internally."
            inputProps={register("isSystem")}
          />

          {/* Free Inclusion — marketing carts only */}
          {productType === "marketing" && (
            <div className="space-y-3">
              <CheckboxRow
                id="isFreeInclusion"
                label="Free Inclusion"
                description="Adds itself to the cart free of charge once the same contractor company already has one of the trigger categories in it — e.g. Dusk & Golden Hour thrown in with that company's photo job. Claimed once per property, across both carts."
                inputProps={register("isFreeInclusion")}
              />

              {isFreeInclusion && (
                <div className="ml-7 space-y-2">
                  <label className="form-label">Free With *</label>
                  <Controller
                    name="freeWithCategories"
                    control={control}
                    rules={{
                      validate: (value, values) =>
                        !values.isFreeInclusion ||
                        (value || []).length > 0 ||
                        "Pick at least one trigger category",
                    }}
                    render={({ field }) => (
                      <SearchSelect
                        options={getCategoryOptions()}
                        value={getCategoryOptions().filter((o) =>
                          (field.value || []).includes(o.value),
                        )}
                        onChange={(opts) =>
                          field.onChange((opts || []).map((o) => o.value))
                        }
                        placeholder="Select trigger categories..."
                        className={errorBorderClass(errors.freeWithCategories)}
                        multiple
                      />
                    )}
                  />
                  {errors.freeWithCategories && (
                    <p className="form-error-message">
                      {errors.freeWithCategories.message}
                    </p>
                  )}
                  {!contractorCompanyId && (
                    <p className="form-error-message">
                      Set a contractor company above — a free inclusion is
                      scoped to one, so it never fires without it.
                    </p>
                  )}
                  {Number(watch("price")) > 0 && (
                    <p className="text-sm text-darkergray">
                      Price is above zero. The cart charges the catalogue price,
                      so set it to 0 for this to actually be free.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </FormSection>

        {/* Notify Emails on Purchase */}
        <FormSection
          title="Purchase Notifications"
          description="Admin-only. These addresses receive a notification each time this product is purchased."
        >
          <div className="space-y-2">
            {/* Tag chips */}
            {notifyEmails.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {notifyEmails.map((email, i) => (
                  <span
                    key={i}
                    className="flex items-center gap-1 px-3 py-1 bg-lightgray text-darkergray rounded-full text-sm"
                  >
                    {email}
                    <button
                      type="button"
                      onClick={() => removeNotifyEmail(i)}
                      className="ml-1 text-darkgray hover:text-black leading-none"
                      aria-label={`Remove ${email}`}
                    >
                      &times;
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Input row */}
            <div className="flex gap-2">
              <input
                type="email"
                value={notifyEmailInput}
                onChange={(e) => setNotifyEmailInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === ",") {
                    e.preventDefault();
                    addNotifyEmail();
                  }
                }}
                placeholder="email@example.com — press Enter or comma to add"
                className="form-input flex-1 border border-mediumgray"
              />
              <button
                type="button"
                onClick={addNotifyEmail}
                className="gray-button whitespace-nowrap"
              >
                Add
              </button>
            </div>
          </div>
        </FormSection>

        {/* Submit Button */}
        <div className="flex justify-end border-t border-mediumgray pt-4">
          <button type="submit" className="black-button">
            {initialData._id ? "Update Product" : "Create Product"}
          </button>
        </div>
      </form>

      <Modal
        isOpen={showCopyModal}
        onClose={() => setShowCopyModal(false)}
        title={`Copy ${copyType} from existing product`}
      >
        <div className="space-y-2 max-h-60 overflow-y-auto">
          {existingProducts.map((product) => (
            <button
              key={product._id}
              type="button"
              onClick={() => copyFromProduct(product._id, copyType)}
              className="w-full text-left p-3 border border-mediumgray rounded-lg hover:bg-lightergray transition-colors"
            >
              <div className="font-medium">{product.name}</div>
              <div className="text-xs text-darkergray mb-1">
                {product.type} • {product.category}
              </div>
              {product[copyType] && product[copyType].length > 0 && (
                <div className="text-sm text-darkergray mt-1">
                  {product[copyType].length} {copyType} •{" "}
                  {product[copyType].slice(0, 2).join(", ")}
                  {product[copyType].length > 2 && "..."}
                </div>
              )}
            </button>
          ))}
        </div>
      </Modal>
    </>
  );
}
